import React, { useEffect, useMemo, useRef } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { FormControl, FormHelperText, styled, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import FormDivider from "@components/FormDivider/FormDivider";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import Select from "@components/Select/Select";
import { t } from "i18next";
import debounce from "lodash.debounce";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import type { FieldErrors, Path } from "react-hook-form";
import Autocomplete from "../Autocomplete/Autocomplete";

export type FieldDef = {
  key: string;
  label: string | ((rowIndex: number) => string);
  type: "text" | "number" | "select" | "date-picker" | "autocomplete" | "textarea";
  options?: { label: string; value: string }[];
  maxChars?: number;
};

interface Props<T> {
  name: Path<T> | string;
  numberOfColumns?: number;
  sectionLabel: string;
  label: string;
  hideItemIndexLabel?: boolean;
  fields: FieldDef[];
  minItems?: number;
  maxItems?: number;
  addButtonLabel?: string;
  onFieldChange: (key: string, value: T[keyof T][]) => void;
  testId?: string;
  autocompleteValue?: { label: string; value: string } | null;
  maxChars?: number;
  minRows?: number;
  slotProps?: {
    htmlInput?: React.InputHTMLAttributes<HTMLInputElement>;
  };
}
const isPrintableKey = (e: React.KeyboardEvent) =>
  e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

const helperTextSx = { ml: 0, minHeight: "20px" };

const RemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));

export function RepeatableObjectField<T extends Record<string, any>>({
  name,
  numberOfColumns = 2,
  sectionLabel,
  label,
  hideItemIndexLabel = false,
  fields: defs,
  minItems = 1,
  maxItems,
  addButtonLabel = "Add",
  onFieldChange,
  testId,
  autocompleteValue,
  maxChars,
  minRows = 6,
  slotProps,
}: Readonly<Props<T>>) {
  const {
    control,
    getValues,
    setValue,
    setError,
    trigger,
    formState: { errors },
  } = useFormContext<T>();

  const { fields, append, remove } = useFieldArray<T>({ control, name: name as T[keyof T] });
  const didHydrate = useRef(false);

  // Debounced sync for non-select edits
  const debouncedSync = useMemo(
    () =>
      debounce((arr: T[keyof T][]) => {
        onFieldChange(String(name), arr as T[keyof T][]);
      }, 300),
    [name, onFieldChange],
  );
  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);

  // Immediate sync helper
  const syncImmediate = () => {
    const arr = getValues(name as T[keyof T]) as T[keyof T][];
    // @ts-expect-error No need to check type here, we know it's T[keyof T][]
    setValue(name as Path<T>, arr, { shouldValidate: true, shouldDirty: true });
    onFieldChange(String(name), arr as T[keyof T][]);
  };

  const handleTrimBlur = (rowIndex: number, fieldKey: string, onBlur?: () => void) => {
    const fieldPath = `${String(name)}.${rowIndex}.${fieldKey}` as Path<T>;
    const rows = getValues(name as Path<T>) as T[keyof T][];
    const previousValue = String(rows[rowIndex][fieldKey]);
    const trimmedValue = previousValue.trim();

    if (trimmedValue !== previousValue) {
      debouncedSync.cancel();

      setValue(fieldPath, trimmedValue as any, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      const nextRows = getValues(name as Path<T>) as T[keyof T][];
      onFieldChange(name as string, nextRows as T[keyof T][]);
    }

    onBlur?.();
  };

  // Hydrate once
  useEffect(() => {
    didHydrate.current = true;
    const arr: T[keyof T][] = (getValues(name as T[keyof T]) as T[keyof T][]) || [];
    if (arr.length < minItems) {
      const blanks = new Array(minItems - arr.length).fill(
        defs.reduce((a, d) => ({ ...a, [d.key]: "" }), {}) as T[keyof T][],
      );
      const next: T[keyof T][] = [...arr, ...blanks];
      // @ts-expect-error No need to check type here, we know it's T[keyof T][]
      setValue(name as Path<T>, next, { shouldValidate: true, shouldDirty: true });
      onFieldChange(String(name), next as T[keyof T][]);
    }
  }, []);

  return (
    <FormControl
      fullWidth
      sx={(theme) => ({
        "mb": 4,
        "& .MuiFormLabel-root": { color: theme.palette.text.primary },
        "& .MuiFormLabel-root.Mui-focused": { color: theme.palette.text.primary },
        "& .MuiFormLabel-root.Mui-error": { color: theme.palette.text.primary },
      })}
    >
      <FormSection
        title={sectionLabel}
        isFullWidth
        button={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              const blank = defs.reduce((a, d) => ({ ...a, [d.key]: "" }), {}) as T[keyof T];
              append(blank);
              syncImmediate();
            }}
            variant="outlined"
            label={<span className="normal-case font-medium">{addButtonLabel}</span>}
            testId={`${testId}-add-button`}
            disabled={!!maxItems && fields.length >= maxItems}
          />
        }
      >
        <div className="space-y-4">
          {fields.map((item, idx) => (
            <React.Fragment key={idx}>
              {(idx > 0 || fields.length > 1) && (
                <>
                  {idx > 0 && <FormDivider />}
                  <div className="col-span-2 flex justify-between pt-8">
                    {hideItemIndexLabel ? (
                      <span />
                    ) : (
                      <Typography variant="body2">{`${label} ${idx + 1}`}</Typography>
                    )}

                    {fields.length > 1 && (
                      <RemoveButton
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          remove(idx);
                          syncImmediate();
                        }}
                        disableElevation
                        variant="contained"
                        label={<span className="normal-case font-medium">Remove</span>}
                        testId={`${testId}-close-button`}
                      />
                    )}
                  </div>
                </>
              )}
              <div key={item.id} className="grid gap-4 mb-8 grid-cols-1">
                <div
                  className={`col-span-3 grid grid-cols-${numberOfColumns} md:grid-cols-${numberOfColumns} gap-4`}
                >
                  {defs.map((def) => (
                    <div className="grid grid-cols-1" key={String(def.key)}>
                      {(() => {
                        const fieldErrorMessage = (errors?.[name] as unknown as FieldErrors[])?.[
                          idx
                        ]?.[def.key]?.message as string | undefined;

                        return (
                          <>
                            {def.label && (
                              // </FormLabel>
                              <CustomFormLabel
                                data-testid={`${def.key}-${idx}`}
                                sx={{ mb: 1 }}
                                error={
                                  !!(errors?.[name] as unknown as FieldErrors[])?.[idx]?.[def.key]
                                }
                              >
                                {typeof def.label === "function" ? def.label(idx) : def.label}
                              </CustomFormLabel>
                            )}
                            <Controller
                              name={`${name as string}.${idx}.${String(def.key)}` as Path<T>}
                              control={control}
                              defaultValue={(item as T[keyof T])[def.key] || ""}
                              render={({ field }) => {
                                const resolvedLabel =
                                  typeof def.label === "function" ? def.label(idx) : def.label;
                                const commonProps = {
                                  placeholder: resolvedLabel,
                                  ...field,
                                };
                                const fieldMaxChars = def.maxChars;
                                const inputSlotProps = def.maxChars
                                  ? { htmlInput: { maxLength: def.maxChars, type: def.type } }
                                  : undefined;
                                // choose sync strategy per type
                                const doSync = (
                                  val: string | number | { label: string; value: string },
                                ) => {
                                  field.onChange(val);
                                  const nextArr = (
                                    getValues(name as T[keyof T]) as T[keyof T][]
                                  ).map((row, i) => (i === idx ? { ...row, [def.key]: val } : row));
                                  if (def.type === "select") {
                                    // immediate for selects
                                    syncImmediate();
                                  } else {
                                    // debounced for others
                                    debouncedSync(nextArr);
                                  }
                                };

                                switch (def.type) {
                                  case "select":
                                    return (
                                      <Select
                                        {...commonProps}
                                        options={def.options || []}
                                        testId={`${String(name)}-${String(def.key)}-${idx}`}
                                        onChange={(option) => {
                                          doSync(option);
                                        }}
                                      />
                                    );

                                  case "autocomplete": {
                                    const selectedOption =
                                      autocompleteValue !== undefined
                                        ? autocompleteValue
                                        : def?.options?.find(
                                            (option) => option.value === field.value,
                                          ) || null;
                                    return (
                                      <Autocomplete
                                        {...commonProps}
                                        error={
                                          !!(errors?.[name] as unknown as FieldErrors[])?.[idx]?.[
                                            def.key
                                          ]
                                        }
                                        value={selectedOption}
                                        testId={`${String(name)}-${String(def.key)}-${idx}`}
                                        options={def.options || []}
                                        onChange={(option) => {
                                          if (option) {
                                            doSync(option.value);
                                          } else {
                                            doSync("");
                                          }
                                          const fieldPath =
                                            `${String(name)}.${idx}.${String(def.key)}` as Path<T>;
                                          void trigger(fieldPath);
                                        }}
                                      />
                                    );
                                  }

                                  case "textarea":
                                    return (
                                      <Input
                                        {...commonProps}
                                        multiline
                                        minRows={minRows}
                                        fullWidth
                                        variant="outlined"
                                        onChange={(e) => {
                                          doSync(e.target.value);
                                        }}
                                        onBlur={handleTrimBlur.bind(
                                          null,
                                          idx,
                                          String(def.key),
                                          commonProps.onBlur,
                                        )}
                                        slotProps={{
                                          root: {
                                            className:
                                              "rounded-lg border border-gray-500 text-sm px-4 py-2",
                                          },
                                          htmlInput: {
                                            ...slotProps?.htmlInput,
                                            maxLength: maxChars,
                                          },
                                          ...slotProps,
                                        }}
                                        sx={{
                                          "& .MuiInputBase-root": {
                                            borderRadius: "8px",
                                            borderColor: "#6B7280",
                                            padding: "12px",
                                            fontSize: "14px",
                                          },
                                          "& textarea": { padding: 0 },
                                        }}
                                        testId={`${String(name)}-${String(def.key)}-${idx}`}
                                      />
                                    );

                                  case "number": {
                                    // For number type, prevent decimals
                                    // Allow user to type freely - validation will catch max digits via Zod
                                    return (
                                      <Input
                                        {...commonProps}
                                        testId={`${String(name)}-${String(def.key)}-${idx}`}
                                        error={
                                          !!(errors?.[name] as unknown as FieldErrors[])?.[idx]?.[
                                            def.key
                                          ]
                                        }
                                        slotProps={inputSlotProps}
                                        onKeyDown={(e) => {
                                          // Block decimals and invalid number characters only
                                          const invalidKeys = ["e", "E", "+", "-", ".", ","];
                                          if (invalidKeys.includes(e.key)) {
                                            e.preventDefault();
                                            return;
                                          }

                                          if (!fieldMaxChars || !isPrintableKey(e)) return;

                                          const input = e.currentTarget as HTMLInputElement;
                                          const current = String(field.value);
                                          const selectionStart =
                                            input.selectionStart ?? current.length;
                                          const selectionEnd = input.selectionEnd ?? current.length;
                                          const selectedLength = Math.max(
                                            selectionEnd - selectionStart,
                                            0,
                                          );
                                          const nextLength = current.length - selectedLength + 1;

                                          if (nextLength > fieldMaxChars) {
                                            e.preventDefault();
                                            setError(
                                              `${String(name)}.${idx}.${String(def.key)}` as Path<T>,
                                              {
                                                type: "max",
                                                message: t("form-validation.max-length", {
                                                  label: def.label,
                                                  max: fieldMaxChars,
                                                }),
                                              },
                                            );
                                          }
                                        }}
                                        onChange={(e) => {
                                          // Remove any decimals that might be pasted
                                          let value = e.target.value;
                                          // Remove decimals, commas, and other invalid characters
                                          value = value.replace(/[.,eE+-]/g, "");

                                          field.onChange(value);

                                          // Update the field value and trigger immediate validation
                                          const fieldPath =
                                            `${String(name)}.${idx}.${String(def.key)}` as Path<T>;
                                          setValue(fieldPath, value as any, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true,
                                          });

                                          // Update the array and sync
                                          const nextArr = (
                                            getValues(name as Path<T>) as T[keyof T][]
                                          ).map((row, i) =>
                                            i === idx ? { ...row, [def.key]: value } : row,
                                          );
                                          onFieldChange(String(name), nextArr as T[keyof T][]);
                                        }}
                                        onBlur={handleTrimBlur.bind(
                                          null,
                                          idx,
                                          String(def.key),
                                          commonProps.onBlur,
                                        )}
                                      />
                                    );
                                  }

                                  // you can add other MUI component types here...
                                  case "text":
                                  default:
                                    return (
                                      <Input
                                        {...commonProps}
                                        testId={`${String(name)}-${String(def.key)}-${idx}`}
                                        error={
                                          !!(errors?.[name] as unknown as FieldErrors[])?.[idx]?.[
                                            def.key
                                          ]
                                        }
                                        slotProps={inputSlotProps}
                                        onKeyDown={(e) => {
                                          if (!fieldMaxChars) return;
                                          if (!isPrintableKey(e)) return;
                                          const rows = getValues(name as Path<T>) as T[keyof T][];
                                          const current = String(rows[idx][def.key]);
                                          if (current.length >= fieldMaxChars) {
                                            e.preventDefault();
                                            setError(
                                              `${String(name)}.${idx}.${String(def.key)}` as Path<T>,
                                              {
                                                type: "max",
                                                message: t("form-validation.max-length", {
                                                  label: def.label,
                                                  max: fieldMaxChars,
                                                }),
                                              },
                                            );
                                          }
                                        }}
                                        onChange={(e) => doSync(e.target.value)}
                                        onBlur={handleTrimBlur.bind(
                                          null,
                                          idx,
                                          String(def.key),
                                          commonProps.onBlur,
                                        )}
                                      />
                                    );
                                }
                              }}
                            />
                            <FormHelperText
                              error={Boolean(fieldErrorMessage)}
                              sx={helperTextSx}
                              data-testid={`${String(name)}-${String(def.key)}-${idx}-helper-text`}
                            >
                              {fieldErrorMessage ?? "\u00A0"}
                            </FormHelperText>
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </FormSection>
    </FormControl>
  );
}

export default RepeatableObjectField;
